import { useQuery } from '@apollo/client'
import _ from 'lodash'
import { useMemo } from 'react'

import { getBrand } from 'pared/utils/brand'
import {
  toFormattedInteger,
  toPercentString,
  toUsdStr,
} from 'pared/utils/number'

import {
  IListLocationLossPreventionKpisType,
  LIST_LOCATION_LOSS_PREVENTION_KPIS,
} from '../gqls'
import useLossPreventionConfig, {
  IKpiSettingType,
} from './useLossPreventionConfig'

const useKpis = (locationId: number, startDate?: string, endDate?: string) => {
  const brand = getBrand()
  const config = useLossPreventionConfig(brand)
  const metrics = useMemo(
    () =>
      config.kpiSettings.reduce(
        (result, k) =>
          k.category === 'basic' && k.fromMetric
            ? [...result, k.fromMetric]
            : result,
        [] as string[],
      ),
    [config.kpiSettings],
  )

  const { data } = useQuery<IListLocationLossPreventionKpisType>(
    LIST_LOCATION_LOSS_PREVENTION_KPIS,
    {
      variables: {
        iStartDate: startDate,
        iEndDate: endDate,
        iFilter: { location_ids: [locationId], metrics },
        useMetrics: metrics.length > 0,
      },
      skip: !startDate || !endDate || !locationId,
    },
  )

  return useMemo(() => {
    const rawData = data?.listLocationLossPreventionKpis?.nodes?.[0]
    const metricData =
      data?.listLocationMetricValues?.nodes?.[0].metricSummaryData

    if (!rawData) return []

    const kpis = config?.kpiSettings?.map((kpi: IKpiSettingType) => {
      if (kpi.category === 'basic') {
        const value = (() => {
          if (kpi.fromMetric) {
            const value = metricData?.[kpi.fromMetric].value

            if (_.isNil(value)) return '-'

            return toUsdStr(value / 100.0)
          }

          switch (kpi.key) {
            case 'cashOverOrShort':
              return toUsdStr(rawData?.cashOverOrShort / 100.0)
            case 'oloOverOrShort':
              return toUsdStr(rawData?.oloOverOrShort / 100.0)
            case 'totalComps':
              return toUsdStr(rawData?.totalComps / 100.0)
            case 'inaccurateCashierShiftPercent':
              return toPercentString(rawData?.inaccurateCashierShiftPercent)
            case 'inaccurateCashierShiftCount':
              return rawData?.inaccurateCashierShiftCount
            case 'compsPercent':
              return toPercentString(rawData?.compsPercent)
          }
        })()

        return {
          key: kpi.key,
          value,
          text: kpi.title,
        }
      } else if (kpi.category === 'custom') {
        const data = rawData?.lossPreventionItems?.find(
          (item) => item.type === kpi.type,
        )
        const value = (() => {
          switch (kpi.key) {
            case 'amount':
              return toUsdStr((data?.amount || 0) / 100.0 || '')
            case 'count':
              return toFormattedInteger(data?.count || '')
            case 'checkPercent':
              return toPercentString(data?.checkPercent || '')
          }
        })()

        return {
          key: kpi.type,
          value,
          text: kpi.title,
        }
      } else {
        return { key: '', value: '', text: '' }
      }
    })

    return kpis
  }, [data, locationId, startDate, endDate])
}

export default useKpis
